import { useTranslation } from "react-i18next";
import { usePlan, type PlanUsage } from "@/hooks/usePlan";
import { openPlans } from "@/lib/plans";
import { testIds } from "@/testIds";

export type AskAllowanceState = "quiet" | "near" | "used";

/** Silent below 80%, a note from 80%, used at 100%; quiet whenever a used allowance doesn't stop Ask. */
export function askAllowanceState(ask: PlanUsage["ask"] | undefined): AskAllowanceState {
  if (!ask?.enforced || ask.unlimited) return "quiet";
  return ask.fraction >= 1 ? "used" : ask.fraction >= 0.8 ? "near" : "quiet";
}

/**
 * 4f · Ask Lilia as progress through a known allowance (launch redesign, Olivia 14 Oct): silent
 * until 80%, a one-line note from 80%, a card in place of the box at 100%, with the date it renews.
 * Never counts, never a balance. Only while a used allowance actually stops Ask Lilia.
 */
export function useAskAllowance() {
  const { data: plan } = usePlan();
  const ask = plan?.ask;
  const state = askAllowanceState(ask);
  return { state, resetsAt: ask?.resetsAt ?? null, free: plan?.plan.slug === "free" };
}

function useRenewal(resetsAt: string | null) {
  const { i18n } = useTranslation();
  return resetsAt ? new Date(resetsAt).toLocaleDateString(i18n.language, { day: "numeric", month: "long" }) : "";
}

export function AskAllowanceNote({ resetsAt }: { resetsAt: string | null }) {
  const { t } = useTranslation("documents");
  const date = useRenewal(resetsAt);
  return (
    <p data-testid={testIds.plan.askAllowanceNote} className="mb-1.5 px-1 text-[12px] text-[var(--lilia-text-secondary)]">
      {t("plan.ask.near", { date })}
    </p>
  );
}

export function AskAllowanceCard({ resetsAt, free }: { resetsAt: string | null; free: boolean }) {
  const { t } = useTranslation("documents");
  const date = useRenewal(resetsAt);
  return (
    <div
      data-testid={testIds.plan.askAllowanceUsed}
      className="mb-3 rounded-[14px] border border-[var(--lilia-border,#e4e9f1)] bg-[var(--lilia-surface,#ffffff)] px-4 py-3"
    >
      <p className="text-[13.5px] font-semibold text-[var(--lilia-text)]">{t("plan.ask.usedTitle")}</p>
      <p className="mt-1 text-[12.5px] leading-[1.5] text-[var(--lilia-text-secondary)]">
        {t("plan.ask.usedBody", { date })}
        {free ? ` ${t("plan.ask.usedFree")}` : ""}
      </p>
      {free && (
        <button
          type="button"
          data-testid={testIds.plan.seePlans}
          data-from="ask"
          onClick={openPlans}
          className="mt-2 text-[12.5px] font-medium text-[var(--lilia-primary)] hover:underline"
        >
          {t("plan.seePlans")}
        </button>
      )}
    </div>
  );
}
